// Self-host Roboto via the @fontsource npm package. Roboto is a sans-serif,
// not a monospace - see the comment on --mono in index.css for why we ditched
// monospace entirely. Tabular-nums declarations on .stat .value, .tbl, etc.
// preserve column alignment without needing fixed-width glyphs.
//
// Side-effect import (no symbols imported, just the @font-face CSS injected
// into the bundle). Order matters: must come BEFORE './index.css' so the
// @font-face is registered before our --mono variable gets used downstream.
//
// Patch DK: narrowed from `@fontsource/roboto` (which registers @font-face
// for all 8 unicode subsets - latin, latin-ext, cyrillic, cyrillic-ext,
// greek, vietnamese, symbols, math - at weight 400) to just the latin
// subset. stackbtc is an English-language financial app; the seven other
// subsets only ever rendered if a user typed/displayed a Cyrillic, Greek,
// Vietnamese, or math symbol character in a --mono-rendered surface, which
// in practice never happens. The browser's unicode-range machinery
// already prevented downloading the unused files, so user-visible
// download savings are small, but the unused @font-face declarations no
// longer waste CSS parse time and the build asset list shrinks from 16
// font files (8 subsets x 2 formats) to 2 (latin x woff/woff2). Roboto
// still renders identically for every character the app actually uses.
import './prospect.js';
import '@fontsource/roboto/latin-400.css';
import './index.css';
// Patch DO: global touch-action / user-select rules for all recharts
// charts on mobile + mobile PWA. Imported AFTER index.css so any future
// recharts-targeted rule in the design system loses to these on equal
// specificity. See file header for the failure modes this fixes.
import './chart-touch.css';
// Patch DT: JS-side companion that calls preventDefault on touchmove
// when the gesture started inside a chart. Defense-in-depth alongside
// the CSS touch-action: none rule for iOS Safari PWA cases where CSS
// is not honored consistently. See chart-touch.ts file header.
import './chart-touch.js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root');
createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Patch DL: register the service worker for repeat-visit caching. The
// guard is two-part:
//   - 'serviceWorker' in navigator: rejects browsers that do not
//     implement SW at all (very rare in 2026, but no-op fallback is
//     correct behavior).
//   - import.meta.env.PROD: skip registration entirely during Vite dev
//     server runs, where caching the dev bundle would defeat
//     hot-reload and trap stale modules across rebuilds.
//
// Registered on the window 'load' event so it never blocks first paint
// or initial JS parse - the browser finishes loading the page first,
// then schedules SW registration during idle time. On first visit the
// SW installs in the background and starts caching after the page is
// already interactive; on subsequent visits the SW is already active
// and serving cached assets before this code even runs.
//
// The fire-and-forget `void` on .register() is intentional - we do
// not need to await registration, and we explicitly catch failures
// because a SW registration error should never crash the app (e.g.
// some browsers reject SW under specific privacy modes).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Registration failed - probably running in a privacy mode or
      // very old browser. The app works fine without the SW; we just
      // do not get the offline / repeat-visit speedup.
    });
  });
  // Reload when the SW takes over a new version so the page never runs
  // stale JS from a pre-deploy stale-while-revalidate cache hit.
  // 4-second delay: the SW activates as soon as it installs (skipWaiting),
  // which can happen while Vercel is still propagating new chunks to edge
  // nodes. An immediate reload would then get the new index.html but
  // 404 on the new hashed chunks. 4 seconds is enough for Vercel's CDN
  // to settle after a deploy; the stale JS running during the wait is
  // harmless since it was already running successfully before the SW swap.
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === 'SW_UPDATED') {
      setTimeout(() => { window.location.reload(); }, 4000);
    }
  });
}
